import { Plus } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { ChampionshipListItem } from 'modules/championship/domain/interfaces/championship-list-item';
import { clearSelectedChampionship } from 'modules/championship/store/championship/actions';
import FieldBackground from 'modules/championship/ui/components/field-background';
import { ChampionshipsBody } from 'modules/championship/ui/components/championships-body/championships-body';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';

import ChampionshipsFilters from '../components/championships-filters';
import { useChampionshipsList } from '../hooks/use-championships-list';

const ChampionshipsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setHeaderContent } = useMainLayout();
  const { info, isLoading: isLoadingOrganizer } = useOrganizer();

  const { championships, years, filters, changeFilter, isLoading, pages } = useChampionshipsList();

  const goToTournamentCreate = useCallback(() => {
    dispatch(clearSelectedChampionship());
    navigate(Pages.CHAMPIONSHIPS.CREATE);
  }, [dispatch, navigate]);

  const resetFilters = useCallback(() => {
    changeFilter({ search: undefined, year: undefined, page: 1 });
  }, [changeFilter]);

  const goToEdit = useCallback(
    (id: ChampionshipListItem['id']) => {
      navigate(Pages.CHAMPIONSHIPS.builders.edit(id));
    },
    [navigate],
  );

  const championshipsWithCity = useMemo(() => {
    return championships?.map((championship) => {
      const championshipAddress = championship?.locations?.[0] && {
        city: championship?.locations?.[0]?.address?.city,
        country: championship?.locations?.[0]?.address?.country,
      };

      return { ...championship, address: championshipAddress ?? info?.address };
    });
  }, [info?.address, championships]);

  useEffect(() => {
    setHeaderContent(
      <>
        <UDHeaderTitle>Турниры</UDHeaderTitle>
        <UDButton variant="primary" className="ml-6 flex items-center" onClick={goToTournamentCreate}>
          <Plus size={16} className="mr-2" color={theme.colors.DarkBlue} />
          {t('championships.createTournamentButtonTitle')}
        </UDButton>
      </>,
    );

    return () => {
      setHeaderContent(null);
    };
  }, [goToTournamentCreate, setHeaderContent, t]);

  return (
    <div className={'relative flex h-full flex-col'}>
      <FieldBackground />
      <ChampionshipsFilters onFilter={changeFilter} filters={filters} years={years} className={'relative z-10'} />
      <ChampionshipsBody
        filters={filters}
        championships={championshipsWithCity}
        pages={pages}
        onDetail={goToEdit}
        onPageChange={(page) => changeFilter({ page })}
        onResetFilters={resetFilters}
        isLoading={isLoading || isLoadingOrganizer}
      />
    </div>
  );
};

export default ChampionshipsListPage;
